import React, { useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Box, Button, HtmlTrans, Typography } from 'web-library';
import Declaration from '../../../components/Declaration';
import { PaymentMethod } from '../../../modules/payment/components/PaymentButton';
import { fallbackEmptyString } from '../../../utils/StringUtils';
import RouteKey from '../../../routes/key';
import { usePostGroupExamRegistrationPayment } from '../../../api-refactored/group-exam-registration/usePostGroupExamRegistrationPayment';
import { ClassesAndTranslation } from '../../../models/ClassesAndTranslation';
import { ExamPaymentModeKey } from '../../../api-refactored/group-exam-registration/usePostReviewGroupExamRegistration';
import GroupExamRegistrationPaymentButton from '../../../modules/payment/components/GroupExamRegistrationPaymentButton';
import { ExamRegistrationType, ExamType } from '../../../api/candidate/exam/model';
import { useGetPaymentAccess } from '../../../api-refactored/financial/useGetPaymentAccess';

const { exam } = RouteKey.child;

const { coordinatorExamRegistrations, coordinatorViewReceipt } = RouteKey.exam.child;

interface IDeclarationAndPaymentButton {
  styling: ClassesAndTranslation;
  selectedPaymentMode: ExamPaymentModeKey;
  hasNoPaymentOptions: boolean;
  totalFee: string;
  examType?: ExamType;
  examRegistrationType?: ExamRegistrationType;
}

const DeclarationAndPaymentButton = ({
  styling,
  selectedPaymentMode,
  hasNoPaymentOptions,
  totalFee,
  examType,
  examRegistrationType = ExamRegistrationType.LATE_SUPPLEMENTARY,
}: IDeclarationAndPaymentButton) => {
  const { classes, t } = styling;
  const [isPaymentDisabled, setIsPaymentDisabled] = useState(false);
  const { uuid: applicationUuid } = useParams<any>();
  const theme = useTheme();
  const postGroupExamPayment = usePostGroupExamRegistrationPayment({});
  const [notDeclaredErrMsg, setNotDeclaredErrMsg] = useState<boolean>(false);
  const [paymentModeNotSelectedErrMsg, setPaymentModeNotSelectedErrMsg] = useState<boolean>(false);
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const [isAgreed, setIsAgreed] = useState<boolean>(false);
  const receiptRoute = `/${exam}/${coordinatorExamRegistrations.route}/${applicationUuid}/${coordinatorViewReceipt.route}`;

  const [paymentAccessDetailsOption, setPaymentAccessDetailsOption] = useState<
    Array<PaymentMethod>
  >([]);

  useGetPaymentAccess({
    onSuccess: (res) => {
      const paymentAccessDetailsArray = [];
      if (res.creditCardMethod) {
        paymentAccessDetailsArray.push(PaymentMethod.ENETS_CREDIT_CARD);
      }
      if (res.payNowMethod) {
        paymentAccessDetailsArray.push(PaymentMethod.PAYNOW_QR);
      }
      setPaymentAccessDetailsOption(paymentAccessDetailsArray);
    },
  });

  const history = useHistory();
  const handleNotDeclaredOrProceedFlow = (callback: () => void) => {
    if (!isAgreed) {
      setNotDeclaredErrMsg(true);
      setPaymentModeNotSelectedErrMsg(false);
    } else {
      setNotDeclaredErrMsg(false);
      setPaymentModeNotSelectedErrMsg(false);
      callback();
    }
  };
  const handleClickPaymentButton = (callback: () => void) => {
    if (!hasNoPaymentOptions) {
      if (selectedPaymentMode === ExamPaymentModeKey.noPaymentModeSelected) {
        setPaymentModeNotSelectedErrMsg(true);
        setNotDeclaredErrMsg(!isAgreed);
      } else {
        handleNotDeclaredOrProceedFlow(callback);
      }
    } else {
      handleNotDeclaredOrProceedFlow(callback);
    }
  };

  const zeroDollarPaymentOption =
    selectedPaymentMode === ExamPaymentModeKey.noPaymentModeSelected &&
    hasNoPaymentOptions &&
    totalFee === '0.00';
  const handlePaymentOption = (callback: (any: string) => void, method?: string) => {
    postGroupExamPayment.mutate(
      {
        uuid: applicationUuid,
        paymentMode: fallbackEmptyString(method),
      },
      {
        onSuccess: (data) => {
          if (
            selectedPaymentMode === ExamPaymentModeKey.candidatePayOption ||
            zeroDollarPaymentOption ||
            data.paymentSuccess
          ) {
            callback(receiptRoute);
          } else {
            callback(data.internalTransactionRef);
          }
        },
        onError: () => {
          callback(`/${exam}/${coordinatorExamRegistrations.route}`);
        },
      }
    );
  };

  const customContent = () => {
    if (examType && examType === ExamType.GCE) {
      return (
        <>
          <HtmlTrans i18nKey="components->>Declaration.CoordinatorGceExamRegistrationContent" />
          <br />
          {t('components->>Declaration.CoordinatorExamRegistrationDeclare')}
        </>
      );
    }
    if (examType && examType === ExamType.IPSLE) {
      if (examRegistrationType === ExamRegistrationType.EXAM) {
        return (
          <>
            <HtmlTrans i18nKey="components->>Declaration.CoordinatorIPsleExamRegistrationContent" />
          </>
        );
      }

      if (
        examRegistrationType === ExamRegistrationType.SUPPLEMENTARY ||
        examRegistrationType === ExamRegistrationType.LATE_SUPPLEMENTARY
      ) {
        return (
          <>
            <HtmlTrans i18nKey="components->>Declaration.CoordinatorIPsleLateRegistrationContent" />
          </>
        );
      }
    }
    return '';
  };

  return (
    <>
      <Box id="declaration">
        <Declaration
          accountType="coordinator"
          title="Declaration"
          isAgreed={isAgreed}
          handleAgreement={() => setIsAgreed(!isAgreed)}
          customContent={customContent()}
        />
        <Box className={classes.errorText}>
          {paymentModeNotSelectedErrMsg && (
            <Typography variant="body1">
              {t('components->>Payment.PleaseSelectPaymentMode')}{' '}
            </Typography>
          )}

          {notDeclaredErrMsg && !isAgreed && (
            <Typography variant="body1">
              {t('components->>Declaration.Please confirm declaration before you proceed.')}
            </Typography>
          )}
        </Box>
      </Box>
      <Box>
        <Button
          id="button-coordinator-review-pay-back"
          className={classes.backButton}
          variant="outlined"
          onClick={() => history.goBack()}
          fullWidth={isTablet}
        >
          {t('exam->>ReviewExam.Back')}
        </Button>
        {(!hasNoPaymentOptions || totalFee === '0.00') && (
          <GroupExamRegistrationPaymentButton
            isUniqueLink={false}
            disabled={isPaymentDisabled}
            onPaymentComplete={(isRetry) => {
              setIsPaymentDisabled(isRetry);
            }}
            selfPayMethodsAvailable={paymentAccessDetailsOption}
            onClickPaymentButton={(callback) => {
              handleClickPaymentButton(() => callback(true));
            }}
            onHandlePaymentMethod={(callback, method) => {
              handlePaymentOption(callback, method);
            }}
            onSuccessPayment={() => history.push(receiptRoute, { selectedPaymentMode })}
            fullWidth={isTablet}
            buttonText={t('exam->>ReviewExam.SubmitRegistration')}
            buttonVariant="contained"
            customConfirmationModalContent={t('exam->>ReviewExam.ConfirmationPrompt')}
            coordinatorExamPaymentMode={selectedPaymentMode}
            zeroDollarPaymentOption={zeroDollarPaymentOption}
            statusPaymentOffline={
              postGroupExamPayment.status !== 'success' ? postGroupExamPayment.status : 'idle'
            }
          />
        )}
      </Box>
    </>
  );
};
export default DeclarationAndPaymentButton;
